/**
 * @see https://confluence.zmags.com/display/EN/Image+slicing+script
 *
 * @param widgets - array of objects: {
 *     top,left,width,height
 * }
 * @param imageHeight - image canvas height
 * @param imageWidth - image canvas width
 * @param addSpacer - add extra cell for each column and row. We need it for rendering email HTML properly
 * @returns {{slice: (function(): [])}}
 */

"use strict";

module.exports = {
    ImageSlicer
};

function ImageSlicer(widgets, imageHeight, imageWidth, addSpacer) {
    return (function() {
        const PAGE_TOP_Y = 0;
        const PAGE_TOP_X = 0;
        const filteredWidgets = _filterInappropriateWidgets(widgets);
        const sortedWidgetsByY = [...filteredWidgets].sort((a, b) => a.top - b.top);
        const sortedWidgetsByX = [...filteredWidgets].sort((a, b) => a.left - b.left);

        function _filterInappropriateWidgets(allWidgets) {
            const outWidgets = allWidgets.map((widget) => {
                widget.left = Math.max(widget.left, 0);
                widget.top = Math.max(widget.top, 0);
                widget.width = widget.left + widget.width < imageWidth ? widget.width : Math.max(imageWidth - widget.left, 0);
                widget.height = widget.top + widget.height < imageHeight ? widget.height : Math.max(imageHeight - widget.top, 0);
                return widget;
            });

            return outWidgets.filter((w) => w.height > 0 && w.width > 0);
        }

        function _createBasicGrid() {
            function getCells(widgets, max, min, coordinate, measure) {
                const cells = [];
                const lines = new Set();
                lines.add(min).add(max);
                widgets.forEach((w) => lines.add(w[coordinate]).add(w[coordinate] + w[measure]));
                const linesArr = Array.from(lines).sort((a, b)=>a - b);
                for (let i = 0; i < linesArr.length - 1; i++) {
                    cells.push({
                        [coordinate]: linesArr[i],
                        [measure]: linesArr[i + 1] - linesArr[i]
                    });
                }
                return cells;
            }
            const rows = getCells(sortedWidgetsByY, imageHeight, PAGE_TOP_Y, "top", "height");
            const cols  = getCells(sortedWidgetsByY, imageWidth, PAGE_TOP_X, "left", "width");
            return {rows, cols};
        }

        function _range(start, count) {
            return Array.apply(0, Array(count))
                .map((element, index) => index + start);
        }
        function _isSpanned(table, td, rowIndex, colIndex) {
            const cellSpannedAbove = !!table.find((row)=>{
                return row.find(({rowspanIndexes = [], colspanIndexes = []}) => {
                    return rowspanIndexes.includes(rowIndex) && colspanIndexes.includes(colIndex);
                });
            });
            const cellSpannedSameRow = !!td.find(({colspanIndexes = []}) => colspanIndexes.includes(colIndex));
            return cellSpannedAbove || cellSpannedSameRow;
        }

        function slice() {
            const {rows, cols} = _createBasicGrid();
            const table = [];
            const widgetsByY = sortedWidgetsByY;
            const widgetsByX = sortedWidgetsByX;
            if (addSpacer) {
                const td = [];
                td.push({
                    width: 1,
                    height: 1,
                    isSpacer: true
                });
                cols.forEach(({width}) => {
                    td.push({
                        width,
                        height: 1,
                        isSpacer: true
                    });
                });
                table.push(td);
            }
            rows.forEach(({top: rowTop, height: rowHeight}, rowIndex) => {
                const td = [];
                let colSpan = 0;
                let rowSpan = 0;
                let colspanIndexes = [];
                let rowspanIndexes = [];
                if (addSpacer) {
                    td.push({
                        width: 1,
                        height: rowHeight,
                        isSpacer: true
                    });
                }
                cols.forEach(({left: colLeft}, colIndex) => {
                    let cellProps = {};
                    const isCellCollapsed = _isSpanned(table, td, rowIndex, colIndex);
                    if (isCellCollapsed) return;

                    const widget = widgetsByY.find((w) => w.left === colLeft && w.top === rowTop);
                    if (widget) {
                        let colSpanIndexTo = cols.findIndex((col) => col.left === widget.left + widget.width);
                        let rowSpanIndexTo = rows.findIndex((row) => row.top === widget.top + widget.height);
                        colSpanIndexTo = colSpanIndexTo > -1 ? colSpanIndexTo : cols.length;
                        rowSpanIndexTo = rowSpanIndexTo > -1 ? rowSpanIndexTo : rows.length;
                        colSpan = colSpanIndexTo - colIndex;
                        rowSpan = rowSpanIndexTo - rowIndex;
                        colspanIndexes = _range(colIndex, colSpan);
                        rowspanIndexes = _range(rowIndex, rowSpan);
                        cellProps = widget;
                    } else {
                        // #### Right extreme
                        let colIsWidget = false;
                        let colIsCollapsed = false;
                        for (let j = colIndex; j <= cols.length - 1; j++) {
                            const {left} = cols[j];
                            colIsWidget = !!widgetsByX.find((w) => w.left === left && w.top === rowTop);
                            colIsCollapsed = _isSpanned(table, td, rowIndex, j);
                            if (!colIsWidget && !colIsCollapsed) {
                                colspanIndexes.push(j);
                            } else {
                                break;
                            }
                        }

                        // #### Bottom extreme
                        const rightBreakpoint = Math.max(...colspanIndexes);
                        const localRightExtreme = cols[rightBreakpoint].left + cols[rightBreakpoint].width;
                        const closestBottomWidget = widgetsByY.find((w) => {
                            return (w.top >= (rowTop + rowHeight))
                                && ((w.left > colLeft &&  w.left < localRightExtreme)
                                    || (w.left + w.width > colLeft && w.left + w.width < localRightExtreme)
                                    || (w.left <= colLeft && w.left + w.width >= localRightExtreme)
                                );
                        });
                        const rowSpanIndexTo = closestBottomWidget
                            ? rows.findIndex((row) => row.top === closestBottomWidget.top)
                            : rows.length;

                        rowSpan = rowSpanIndexTo - rowIndex;
                        rowspanIndexes = _range(rowIndex, rowSpan);
                        cellProps = {
                            top: rowTop,
                            left: colLeft,
                            height: (rows[rowSpanIndexTo - 1].top + rows[rowSpanIndexTo - 1].height) - rowTop,
                            width: (cols[rightBreakpoint].left + cols[rightBreakpoint].width) - colLeft
                        };
                    }

                    td.push(Object.assign({
                        isWidget: !!widget,
                        rowSpan: rowspanIndexes.length,
                        colSpan: colspanIndexes.length,
                        rowspanIndexes,
                        colspanIndexes
                    }, cellProps));
                    colspanIndexes = [];
                    rowspanIndexes = [];
                });

                table.push(td);
            });

            return table;
        }
        return {slice};
    }(widgets, imageHeight, imageWidth));
}



// WEBPACK FOOTER //
// ./src/editor/util/ImageSlicer.js